// Page Instagram — gestion des comptes Instagram connectés.
// Liste les comptes IG du tenant + bouton de connexion + bouton révoquer.

import { Link } from 'react-router-dom';
import { useInstagramAccounts } from '../hooks/useInstagramAccounts';
import { ConnectInstagramButton } from '../components/instagram/ConnectInstagramButton';
import { Footer } from '../components/shared/Footer';

export const Instagram = () => {
  const { accounts, loading, error, revoke } = useInstagramAccounts();

  const handleRevoke = async (id: string, handle: string) => {
    if (!window.confirm(`Déconnecter @${handle} de Falko ?\n\nLes commentaires existants restent, mais Falko ne recevra plus les nouveaux et ne pourra plus répondre depuis ce compte.`)) {
      return;
    }
    try {
      await revoke(id);
    } catch (e) {
      window.alert(`Erreur lors de la déconnexion : ${e instanceof Error ? e.message : 'inconnue'}`);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="text-xl font-semibold text-neutral-900 hover:text-violet-700 transition">
            ← Falko
          </Link>
          <h1 className="text-sm text-neutral-500">Comptes Instagram</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900 mb-2">
            Tes comptes Instagram connectés
          </h2>
          <p className="text-neutral-600">
            Connecte les comptes Instagram (Business ou Créateur) que Falko surveillera.
            Tu peux en connecter plusieurs si tu gères différentes marques.
          </p>
        </div>

        {loading && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-8 text-center text-neutral-500">
            Chargement…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
            <p className="text-red-700 font-medium mb-1">Erreur</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && accounts.length === 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <div className="text-5xl mb-4">📷</div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Aucun compte connecté pour l'instant
            </h3>
            <p className="text-neutral-600 mb-6 max-w-md mx-auto">
              Connecte ton premier compte Instagram pour que Falko commence à recevoir
              tes commentaires et te suggérer des réponses dans ta voix.
            </p>
            <ConnectInstagramButton />
            <p className="text-xs text-neutral-400 mt-4">
              💡 Il te faut un compte Instagram <strong>Business</strong> ou <strong>Créateur</strong>.
            </p>
          </div>
        )}

        {!loading && !error && accounts.length > 0 && (
          <>
            <ul className="space-y-3 mb-6">
              {accounts.map((acc) => (
                <li
                  key={acc.id}
                  className="bg-white rounded-2xl border border-neutral-200 p-5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-lg">
                      {acc.handle[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900">@{acc.handle}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        <StatusBadge status={acc.status} /> · Token expire le{' '}
                        {new Date(acc.tokenExpiresAt).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevoke(acc.id, acc.handle)}
                    className="text-sm text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-lg transition"
                  >
                    Déconnecter
                  </button>
                </li>
              ))}
            </ul>
            <div className="text-center">
              <ConnectInstagramButton label="+ Connecter un autre compte" />
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

const StatusBadge = ({ status }: { status: 'ACTIVE' | 'EXPIRED' | 'DISCONNECTED' }) => {
  const config = {
    ACTIVE: { color: 'bg-green-100 text-green-700', label: 'Actif' },
    EXPIRED: { color: 'bg-orange-100 text-orange-700', label: 'Expiré' },
    DISCONNECTED: { color: 'bg-neutral-100 text-neutral-600', label: 'Déconnecté' },
  }[status];
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};
